import { z } from '../zod.js';
import { CHARGE_STATUS } from '../charge/enum';

export const ORDER_TYPE = Object.freeze({
  purchase: 'purchase',
  submission: 'submission',
} as const);

export const OrderTypeValidationEnum = z.enum(ORDER_TYPE);

export const ORDER_LINE_ITEM_TYPE = Object.freeze({
  product: 'product',
  setup_fee: 'setup_fee',
  ticket: 'ticket',
  ticket_fee: 'ticket_fee',

  form: 'form',
} as const);

export const OrderLineItemTypeValidationEnum = z.enum(ORDER_LINE_ITEM_TYPE);

/**
 * The Order's payment lifecycle. Every charge/subscription/submission now gets
 * an Order (drafts included), so this is an EXACT mirror of the full
 * CHARGE_STATUS vocabulary — derived member-by-member so a rename there breaks
 * the build here. Subscription-only statuses fold onto their nearest charge
 * equivalent in mapSubscriptionOutcome (`incomplete` → `created`,
 * `incomplete_expired` → `failed`), keeping this vocabulary charge-shaped.
 * Not to be confused with `Order.merchantStatus` / `Charge.orderStatus`
 * (archived/active/canceled), which is a merchant workflow state.
 *
 * The refund states are lifecycle states here on purpose: a refund does not just
 * move money back (Charge and the Transaction ledger record that), it un-does
 * the purchase itself — entitlement revocation reads the order — so "show me
 * refunded orders" is an order-list filter. `refunded` means everything paid was
 * returned; `partial_refund` means some was. No writer yet: refund flows update
 * Charge and the ledger today, and whatever rolls refunds up onto the Order
 * owes these two states their transitions.
 */
export const ORDER_LIFECYCLE_STATUS = Object.freeze({
  draft: CHARGE_STATUS.draft,
  pending: CHARGE_STATUS.pending,
  unpaid: CHARGE_STATUS.unpaid,
  partially_paid: CHARGE_STATUS.partially_paid,
  paid: CHARGE_STATUS.paid,
  canceled: CHARGE_STATUS.canceled,
  failed: CHARGE_STATUS.failed,
  partial_refund: CHARGE_STATUS.partial_refund,
  refunded: CHARGE_STATUS.refunded,
  draft_expired: CHARGE_STATUS.draft_expired,
  created: CHARGE_STATUS.created,
} as const);

export const OrderLifecycleStatusValidationEnum = z.enum(ORDER_LIFECYCLE_STATUS);
